// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import { type Database, type DatabaseField } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';

import {
  getNumberRepresentation,
  getStringRepresentation,
  isEmptyValue,
} from '../field-types';
import { getConvertedDatabaseValue, getDatabaseEntryRequired } from '../utils';

type SortWithField = DatabaseViewSettings['sort'] & {
  field: DatabaseField;
};

export const isSortWithField = (
  maybeSortWithField: DatabaseViewSettings['sort'] & { field?: DatabaseField }
): maybeSortWithField is SortWithField => {
  return maybeSortWithField.field !== undefined;
};

/**
 * Compares two strings or numbers and returns a negative number if the second one is greater, a positive number if the
 * first one is greater, or zero if both are equal.
 * If both arguments are numbers (in number or string form), they are compared numerically. Otherwise, they are compared
 * naturally (alphabetical segments are compared alphabetically, numerical segments are compared numerically).
 *
 * One implication of this for now is that pure numbers support decimals, negatives and thousands separators, while number
 * segments in mixed values don't. For example, "-10" is less than "-2", while "JIRA-10" is greater than "JIRA-2" (despite
 * the minus sign in both cases). Also, "1.10" is less than "1.2", while "JIRA-1.10" is greater than "JIRA-1.2" (since in
 * the mixed value the number segments are compared separately).
 *
 * Following international standards, a full stop and a comma are supported as decimal separators, and a space and thin space
 * (along with their non-breaking variants) are supported as thousands separators. Full-stops and commas are not supported
 * as thousands separators, as they are impossible to distinguish from decimal separators.
 */
export function compareStringOrNumber(
  numberOrStringA: string | number,
  numberOrStringB: string | number,
  ascending: boolean
): number {
  const [a, b] = ascending
    ? [numberOrStringA, numberOrStringB]
    : [numberOrStringB, numberOrStringA];
  const [numberA, numberB] = [a, b].map((numberOrString) =>
    typeof numberOrString === 'number'
      ? numberOrString
      : Number(
          numberOrString
            .trim()
            .replace(/[,]/g, '.') // Normalize decimal separator
            .replace(/[\u2212]/g, '-') // Normalize minus (Unicode minus)
            .replace(/[ \u2009\u202F\xa0']/g, '') // Remove thousands separators (space, thin space, non-breaking thin space, non-breaking space, apostrophe)
        )
  );

  if (!isNaN(numberA) && !isNaN(numberB)) {
    return numberA - numberB;
  } else {
    return `${a}`.localeCompare(`${b}`, 'en-US', { caseFirst: 'upper', numeric: true });
  }
}

type EntrySortContext<Db extends Database> = {
  databaseDraft: Db;
  sortsWithField: SortWithField[];
  state: DefaultRootState | undefined;
};

const compareEntries = <Db extends Database>(
  entryIdA: string,
  entryIdB: string,
  context: EntrySortContext<Db>
): number => {
  const { databaseDraft, sortsWithField, state } = context;

  if (sortsWithField.length === 0) {
    return 0;
  }

  const sortWithField = sortsWithField[0];
  const sortField = sortWithField.field;

  const entryA = getDatabaseEntryRequired(databaseDraft, entryIdA);
  const entryB = getDatabaseEntryRequired(databaseDraft, entryIdB);
  const contextA = {
    database: databaseDraft,
    field: sortField,
    entry: entryA,
    value: getConvertedDatabaseValue(databaseDraft, sortField, entryA, state),
  };
  const contextB = {
    database: databaseDraft,
    field: sortField,
    entry: entryB,
    value: getConvertedDatabaseValue(databaseDraft, sortField, entryB, state),
  };

  // Ignore empty values when sorting
  const isEmptyValueA = isEmptyValue(contextA, state);
  const isEmptyValueB = isEmptyValue(contextB, state);
  if (isEmptyValueA && isEmptyValueB) {
    return compareEntries(entryIdA, entryIdB, {
      ...context,
      sortsWithField: sortsWithField.slice(1),
    });
  } else if (isEmptyValueA) {
    return 1;
  } else if (isEmptyValueB) {
    return -1;
  }

  const comparison = compareStringOrNumber(
    getNumberRepresentation(contextA, state) ??
      (getStringRepresentation(contextA, state) || ''),
    getNumberRepresentation(contextB, state) ??
      (getStringRepresentation(contextB, state) || ''),
    sortWithField.ascending
  );

  return comparison === 0
    ? compareEntries(entryIdA, entryIdB, {
        ...context,
        sortsWithField: sortsWithField.slice(1),
      })
    : comparison;
};

export function sortEntriesMulti<Db extends Database>(
  databaseDraft: Db,
  entries: string[],
  sortsWithField: SortWithField[],
  state: DefaultRootState | undefined,
  ignoredEntries: string[] = []
) {
  const entriesToSort = entries.filter((entry) => !ignoredEntries.includes(entry));
  const ignoredEntriesWithIndexes = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => ignoredEntries.includes(entry));

  const sortedEntries = entriesToSort.sort((entryIdA, entryIdB) => {
    return compareEntries(entryIdA, entryIdB, {
      databaseDraft,
      sortsWithField,
      state,
    });
  });

  // Reinsert ignored entries in their original positions
  ignoredEntriesWithIndexes.forEach(({ entry, index }) => {
    sortedEntries.splice(index, 0, entry);
  });

  return sortedEntries;
}
